import { useEffect, useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import AppRoutes from './routes/AppRoutes';
import { AuthProvider } from './auth';
import { UIProvider } from './context';
import {
  PWAUpdatePrompt,
  InstallPrompt,
  OfflineIndicator,
  FailedExpensesBanner,
} from './components/pwa';
import { useOnlineStatus } from './hooks/useOnlineStatus';
import { syncPendingExpenses } from './services/offlineSync';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

/**
 * Listens for the custom `sw-update-available` event dispatched from
 * main.tsx and exposes update controls to the PWAUpdatePrompt banner.
 */
function useSWUpdate() {
  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [updateFn, setUpdateFn] = useState<
    ((reloadPage?: boolean) => Promise<void>) | null
  >(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        updateSW: (reloadPage?: boolean) => Promise<void>;
      };
      setUpdateFn(() => detail.updateSW);
      setNeedsUpdate(true);
    };
    window.addEventListener('sw-update-available', handler);
    return () => window.removeEventListener('sw-update-available', handler);
  }, []);

  const applyUpdate = useCallback(async () => {
    if (updateFn) await updateFn(true);
  }, [updateFn]);

  return { needsUpdate, setNeedsUpdate, applyUpdate };
}

function App() {
  const { needsUpdate, setNeedsUpdate, applyUpdate } = useSWUpdate();
  const isOnline = useOnlineStatus();

  useEffect(() => {
    if (!isOnline) return;

    syncPendingExpenses().then((result) => {
      if (result.synced > 0) {
        toast.success(
          `Synced ${result.synced} offline expense${result.synced > 1 ? 's' : ''}`,
        );
        queryClient.invalidateQueries();
      }
      if (result.permanent > 0) {
        toast.error(
          `${result.permanent} expense${result.permanent > 1 ? 's' : ''} failed — check the banner below`,
        );
      }
    });
  }, [isOnline]);

  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8 text-center">
          <div>
            <h1 className="mb-2 text-2xl font-bold text-red-600">Something went wrong</h1>
            <p className="text-gray-600">
              {error instanceof Error ? error.message : 'An unexpected error occurred.'}
            </p>
            <button
              className="mt-4 rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
              onClick={() => window.location.reload()}
            >
              Reload
            </button>
          </div>
        </div>
      )}
    >
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <UIProvider>
              <div className="min-h-screen bg-gray-50">
                <AppRoutes />
                <Toaster position="top-right" />

                {/* PWA UI */}
                <OfflineIndicator />
                <InstallPrompt />
                <FailedExpensesBanner />
                <PWAUpdatePrompt
                  show={needsUpdate}
                  onUpdate={applyUpdate}
                  onDismiss={() => setNeedsUpdate(false)}
                />
              </div>
            </UIProvider>
          </AuthProvider>
          {import.meta.env.VITE_ENABLE_DEVTOOLS === 'true' && (
            <ReactQueryDevtools initialIsOpen={false} />
          )}
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
